import React, { useState, useRef } from 'react';

interface TooltipState {
  x: number;
  y: number;
  text: string;
}

const EssayPreview: React.FC = () => {
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const rafId = useRef<number | null>(null);

  const calculateTooltipPosition = (clientX: number, clientY: number) => {
    const tooltipWidth = 320; // max-width from CSS
    const tooltipHeight = 80; // estimated height
    const padding = 20;
  
    let x = clientX + 15;
    let y = clientY - 15;
  
    // Prevent going off the right edge
    if (x + tooltipWidth + padding > window.innerWidth) {
      x = clientX - tooltipWidth - 15;
    }
  
    // Prevent going off the left edge
    if (x < padding) {
      x = padding;
    }
  
    // Prevent going off the top edge
    if (y < padding) {
      y = clientY + 25;
    }
  
    // Prevent going off the bottom edge
    if (y + tooltipHeight + padding > window.innerHeight) {
      y = clientY - tooltipHeight - 25;
    }
  
    return { x, y };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    const target = e.currentTarget as HTMLElement;
    const commentText = target.getAttribute("data-comment");

    if (commentText) {
      if (rafId.current) return;

      rafId.current = requestAnimationFrame(() => {
        const position = calculateTooltipPosition(e.clientX, e.clientY);
        setTooltip({
          x: position.x,
          y: position.y,
          text: commentText,
        });
        rafId.current = null;
      });
    }
  };

  const handleMouseOut = () => {
    if (rafId.current) {
      cancelAnimationFrame(rafId.current);
      rafId.current = null;
    }
    setTooltip(null);
  };

  return (
    <div className="px-6 pb-24">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
          {/* Preview Header */}
          <div className="border-b border-gray-100 p-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <div className="w-3 h-3 rounded-full bg-green-500" />
            </div>
          </div>
          
          {/* Essay Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 min-h-[600px]">
            {/* Main Essay */}
            <div className="col-span-2 border-r border-gray-100 p-8">
              <div className="mb-6">
                <div className="text-sm text-gray-500 mb-1">Prompt:</div>
                <div className="text-lg text-gray-700 font-bold">Why do you want to attend Duke University?</div>
              </div>
              <div className="prose prose-gray max-w-none">
                <p>
                  <span 
                    className="bg-yellow-50 border-b-2 border-yellow-300"
                    data-comment="Consider a more engaging opening that directly addresses why Duke is your top choice."
                    onMouseMove={handleMouseMove}
                    onMouseOut={handleMouseOut}
                  >
                    Duke represents everything I'm looking for in a university: academic rigor, vibrant campus life, and a strong research focus
                  </span>.
                </p>

                <p className="mt-4">
                  The Pratt School of Engineering's emphasis on 
                  <span 
                    className="bg-yellow-50 border-b-2 border-yellow-300"
                    data-comment="Be more specific about which aspects of innovation interest you. What specific technologies or entrepreneurial programs align with your goals?"
                    onMouseMove={handleMouseMove}
                    onMouseOut={handleMouseOut}
                  > innovation excites me — the opportunity to explore the intersection of technology and entrepreneurship </span>
                  through programs like Duke I&E is exactly what I've been seeking.
                </p>

                <p className="mt-4">
                  Beyond academics, 
                  <span 
                    className="bg-yellow-50 border-b-2 border-yellow-300"
                    data-comment="Great mention of DukeEngage. Consider adding a specific example of a DukeEngage project that interests you and how it aligns with your service goals."
                    onMouseMove={handleMouseMove}
                    onMouseOut={handleMouseOut}
                  > Duke's commitment to service resonates with me. Programs like DukeEngage show that Duke isn't just about learning </span>
                  — it's about using knowledge to drive real-world change. I'm eager to join a community where innovation is paired with purpose.
                </p>

                <p className="mt-4">
                  I also can't help but be drawn to 
                  <span 
                    className="bg-yellow-50 border-b-2 border-yellow-300"
                    data-comment="Strong campus culture references. To make this more impactful, share a specific moment from your campus visit that made you feel this sense of belonging."
                    onMouseMove={handleMouseMove}
                    onMouseOut={handleMouseOut}
                  > the energy of Cameron Indoor, the serenity of the Duke Gardens, and the feeling of belonging that so many students describe </span>
                  . Duke doesn't just offer an education — it offers a home where I can grow intellectually, socially, and personally. That's where I want to be.
                </p>
              </div>
            </div>
            
            {/* Comments Panel */}
            <div className="bg-gray-50 p-6">
              <h3 className="text-lg font-semibold mb-4">AI Feedback</h3>
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                    <span className="text-sm font-medium text-gray-700">Opening Impact</span>
                  </div>
                  <p className="text-sm text-gray-600">The opening is too general and could apply to many universities. Consider starting with a specific Duke moment or unique program that captured your interest.</p>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <span className="text-sm font-medium text-gray-700">Program Specificity</span>
                  </div>
                  <p className="text-sm text-gray-600">Good mention of Pratt and Duke I&E, but could be strengthened by mentioning specific initiatives or projects that align with your interests.</p>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-yellow-500" />
                    <span className="text-sm font-medium text-gray-700">Personal Connection</span>
                  </div>
                  <p className="text-sm text-gray-600">Strong conclusion with specific campus references. Consider weaving in a personal story about your visit to these places to make it more memorable.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Tooltip */}
      {tooltip && (
        <div
          className="fixed z-[9999] bg-gray-800 text-white text-sm font-semibold rounded-lg px-4 py-3 shadow-xl pointer-events-none transition-opacity duration-75 ease-out"
          style={{
            left: `${tooltip.x}px`,
            top: `${tooltip.y}px`,
            maxWidth: "320px",
            lineHeight: "1.5",
            whiteSpace: "pre-wrap",
            wordWrap: "break-word",
          }}
        >
          {tooltip.text}
        </div>
      )}
    </div>
  );
};

export default EssayPreview; 